import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { UiButton, UiIcon, UiSwiper, UiSwiperItem } from 'ui-components';
import DisplayError from 'components/error/DisplayError';
import {
  InstagramStoryModal,
  InstagramStoryItem,
  FixedHelpingArrows,
  Loading,
} from 'components';
import {
  StyledSwiper,
  StyledPrevButton,
  StyledNextButton,
  StyledStorySwiper,
} from './StorySwiper.styles';
import StorySwiperSkeleton from './StorySwiperSkeleton';

const StorySwiper = ({
  stories,
  page,
  setPage,
  seeAllStories = false,
  sendToBulletin,
  showMoreButtonInEnd = false,
  maxItemsToShow = null,
  renderDeleteButton,
}) => {
  const swiperElRef = useRef(null);
  const { t } = useTranslation();

  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const handleNext = () => {
    if (swiperElRef.current) {
      swiperElRef.current.swiper.slideNext();
    }
  };

  const handlePrev = () => {
    if (swiperElRef.current) {
      swiperElRef.current.swiper.slidePrev();
    }
  };

  useEffect(() => {
    const swiperInstance = swiperElRef.current?.swiper;

    if (!swiperInstance) {
      return;
    }
    if (swiperInstance) {
      swiperInstance.on('slideChange', () => {
        setIsBeginning(swiperInstance.isBeginning);
        setIsEnd(swiperInstance.isEnd);
      });
      swiperInstance.on('reachEnd', () => {
        if (!stories?.loading) setPage((prev) => prev + 1);
      });
    }
  }, [swiperElRef.current]);

  return (
    <StyledStorySwiper>
      {!showMoreButtonInEnd && (
        <div className='story-header'>
          <div className='flex'>
            <span className='header-title ml-4'>{t('content:story')}</span>
          </div>

          {seeAllStories && (
            <Link to='../instagram/stories' relative='path'>
              <UiButton variant='outlined' color='primary'>
                {t('content:seeAllStories')}
              </UiButton>
            </Link>
          )}
        </div>
      )}

      {stories?.loading && page === 1 && <StorySwiperSkeleton />}
      {!stories?.loading && stories?.error && (
        <div className='items-list h-20 flex center mb-60'>
          <DisplayError error={stories?.error} />
        </div>
      )}
      {!stories?.loading && !stories?.error && stories?.data?.length <= 0 && (
        <div className='items-list flex center col mb-60 muted'>
          <div className='fs-16 bold muted'>{t('common:dataNotFound')}</div>
        </div>
      )}
      {((!stories?.loading && !stories?.error && stories?.data?.length > 0) ||
        page > 1) && (
        <>
          <StyledSwiper>
            <UiSwiper
              ref={swiperElRef}
              className='story-items'
              onSlideChange={(swiper) => {
                setIsBeginning(swiper.isBeginning);
                setIsEnd(swiper.isEnd);
              }}
            >
              {(maxItemsToShow
                ? stories?.data?.slice(0, maxItemsToShow)
                : stories?.data
              )?.map((story, index) => (
                <UiSwiperItem
                  key={story?.id}
                  class={`instagram-story-item-wrapper ${
                    page === 1 ? 'active-interaction' : ''
                  }`}
                >
                  <InstagramStoryItem story={story} storyIndex={index} />
                </UiSwiperItem>
              ))}

              {showMoreButtonInEnd &&
                maxItemsToShow &&
                stories?.data?.length > maxItemsToShow && (
                  <UiSwiperItem class='instagram-story-item-wrapper show-more-item'>
                    <Link to='../brandbaan/stories' relative='path'>
                      <UiButton
                        variant='outlined'
                        color='primary'
                        size='medium'
                      >
                        {t('content:seeAllStories')}
                      </UiButton>
                    </Link>
                  </UiSwiperItem>
                )}
            </UiSwiper>

            <StyledPrevButton hide={isBeginning} onClick={handlePrev}>
              <UiIcon icon='ri-arrow-right-s-line' size={18} />
            </StyledPrevButton>
            <StyledNextButton hide={isEnd} onClick={handleNext}>
              {stories?.loading ? (
                <Loading spinning={true} unVisibleText={true} />
              ) : (
                <UiIcon icon='ri-arrow-left-s-line' size={18} />
              )}
            </StyledNextButton>
          </StyledSwiper>

          <InstagramStoryModal
            stories={stories?.data}
            sendToBulletin={sendToBulletin}
            renderDeleteButton={renderDeleteButton}
          />
          <FixedHelpingArrows />
        </>
      )}
    </StyledStorySwiper>
  );
};

StorySwiper.propTypes = {
  stories: PropTypes.shape({
    data: PropTypes.array,
    loading: PropTypes.bool,
    error: PropTypes.object,
  }),
  page: PropTypes.number,
  setPage: PropTypes.func,
  seeAllStories: PropTypes.bool,
  sendToBulletin: PropTypes.bool,
  showMoreButtonInEnd: PropTypes.bool,
  maxItemsToShow: PropTypes.number,
  renderDeleteButton: PropTypes.func,
};

export default StorySwiper;
